import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../Slices/authSlice";
import { fetchTrashedTrek } from "../../Slices/trek.slice";
import { fetchTrashedLocations } from "../../Slices/travel.slice";
import { logo } from "assets/assets";
import { IoMdNotifications } from "react-icons/io";
import { FaCaretDown } from "react-icons/fa";
import { RiDeleteBin6Line, RiLogoutBoxRLine, RiTeamLine, RiUserLine } from "react-icons/ri";
import "assets/scss/components/nav.scss";

const formatRoleLabel = (user) => {
    if (user?.isSuperAdmin || user?.role === "superAdmin") return "Super Admin";
    if (user?.role === "districtManager") return "District Manager";
    if (user?.role === "subAdmin") return "Sub Admin";
    if (user?.role === "editor") return "Editor";
    return "Team Member";
};

const Nav = () => {
    const [adminprofileClicked, setAdminprofileClicked] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const popupRef = useRef(null);
    const { user } = useSelector((state) => state.auth);

    const { trashedTrek } = useSelector((state) => state.trek);
    const { trashedLocations } = useSelector((state) => state.travel);

    useEffect(() => {
        dispatch(fetchTrashedTrek());
        dispatch(fetchTrashedLocations());
    }, [dispatch]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setAdminprofileClicked(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const totalTrashedCount = (trashedTrek?.length || 0) + (trashedLocations?.length || 0);
    const initials = useMemo(
        () => (user?.name || user?.id || "AD").slice(0, 2).toUpperCase(),
        [user]
    );

    const landingPath = user?.isSuperAdmin || user?.role === "superAdmin" ? "/dashboard" : "/cms";

    const handleLogout = async () => {
        try {
            await dispatch(logout()).unwrap();
        } catch (_) {
            return;
        }

        localStorage.removeItem("token");
        navigate("/", { replace: true });
    };

    return (
        <header className="adminTopbar">
            <div className="adminTopbar__inner">
                <button
                    type="button"
                    className="adminTopbar__brand"
                    onClick={() => navigate(landingPath)}
                >
                    <span className="adminTopbar__brandLogo">
                        <img src={logo} alt="Trek Tour Travel admin logo" />
                    </span>
                    <span className="adminTopbar__brandCopy">
                        <span className="adminTopbar__eyebrow">Admin Console</span>
                        <strong>Content Management Panel</strong>
                    </span>
                </button>

                <div className="adminTopbar__actions" ref={popupRef}>
                    <button
                        type="button"
                        className="adminTopbar__iconButton"
                        onClick={() => navigate("/cms/notification")}
                    >
                        <IoMdNotifications />
                    </button>

                    <button
                        type="button"
                        className="adminTopbar__iconButton adminTopbar__iconButton--trash"
                        onClick={() => navigate("/cms/trash")}
                    >
                        <RiDeleteBin6Line />
                        {totalTrashedCount > 0 && (
                            <span className="adminTopbar__countBadge">{totalTrashedCount}</span>
                        )}
                    </button>

                    <button
                        type="button"
                        className={`adminTopbar__profileButton ${adminprofileClicked ? "is-open" : ""}`}
                        onClick={() => setAdminprofileClicked((prevState) => !prevState)}
                    >
                        <span className="adminTopbar__avatar">{initials}</span>
                        <span className="adminTopbar__profileCopy">
                            <strong>{user?.name || user?.id || "Profile"}</strong>
                            <small>{formatRoleLabel(user)}</small>
                        </span>
                        <FaCaretDown className="adminTopbar__caret" />
                    </button>

                    {adminprofileClicked && (
                        <div className="adminTopbar__profileMenu">
                            <button
                                type="button"
                                className="adminTopbar__menuAction"
                                onClick={() => {
                                    setAdminprofileClicked(false);
                                    navigate("/profile");
                                }}
                            >
                                <RiUserLine />
                                <span>Profile</span>
                            </button>

                            {user?.role === "districtManager" && user?.id && (
                                <button
                                    type="button"
                                    className="adminTopbar__menuAction"
                                    onClick={() => {
                                        setAdminprofileClicked(false);
                                        navigate(`/roleManager/profile/${user.id}`);
                                    }}
                                >
                                    <RiTeamLine />
                                    <span>My Team</span>
                                </button>
                            )}

                            <button
                                type="button"
                                className="adminTopbar__menuAction adminTopbar__menuAction--danger"
                                onClick={handleLogout}
                            >
                                <RiLogoutBoxRLine />
                                <span>Logout</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Nav;
